import React, {FC, useEffect, useState} from "react";
import {Box, styled} from "@mui/material";
import {useSelector} from "react-redux";
import {AppStateType} from "../../../../../../../../../redux/reducers";
import {Document, Image, Page, PDFViewer, Text, View, StyleSheet, Font} from "@react-pdf/renderer";
import Loading from "../../../../../../../statistic/components/widgets/components/Loading";
import {ICompanyAPIKey} from "../../../../../../../../../models/CompanyAPIKey";
import CodeBlock from "./CodeBlock";
import CodeBlockGTM from "./CodeBlockGTM";
import {CircularProgress, Link} from "@material-ui/core";
import AbsoluteLoading from "../../../../../../../../../components/AbsoluteLoading";
import {Alert} from "@material-ui/lab";

Font.register({
  family: 'BlinkMacSystemFont',
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-light.ttf`,
      fontStyle: 'light'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-medium.ttf`,
      fontStyle: 'medium'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-medium.ttf`,
      fontStyle: 'regular'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-semibold.ttf`,
      fontStyle: 'semibold'
    },
  ]
});


Font.register({
  family: 'SourceCodePro',
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/SourceCodePro-Light.ttf`,
      fontStyle: 'light'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/SourceCodePro-Regular.ttf`,
      fontStyle: 'regular'
    },
  ]
});

const styles = StyleSheet.create({
  medium: {
    fontFamily: 'BlinkMacSystemFont',
    fontStyle: 'medium',
    color: '#172b4d'
  },
  paragraph: {
    fontFamily: 'BlinkMacSystemFont',
    fontStyle: 'light',
    lineHeight: 1.714,
    color: '#172b4d',
    fontSize: 10,
  },
  block: {
    marginTop: 15
  },
  secondPageWrapper: {
    width: '100%',
    padding: '20px 50px 0 50px',
  },
  li: {
    flexDirection: "row",
    marginVertical: 2
  },
  imageWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4
  },
  logo: {
    left: 0,
    width: "auto",
    height: "25px",
    objectFit: "contain",
  }
})

type PropsType = {
  companyAPIKey?: ICompanyAPIKey
  name: string | undefined
}

const GTMInstruction: FC<PropsType> = ({
  companyAPIKey,
  name
}) => {
  const property = useSelector((state: AppStateType) => state.propertyReducer.property)!
  const [loading, setLoading] = useState(true)
  const [pdfRendered, setPdfRendered] = useState(false)

  useEffect(() => {
    if (pdfRendered && property) {
      setLoading(false)
    }
  }, [property, pdfRendered]);

  return (
    <Box position={'relative'}>
      <Alert variant={'filled'} style={{backgroundColor: '#323639'}}>This instruction is suitable for those web developers who have a website connected to <Link href={'https://developers.google.com/tag-platform/tag-manager'} target={'_blank'}>Google Tag Manager</Link></Alert>
      <PDFViewer style={{ width: "100%", height: "100vh" }}>
        <Document onRender={() => setPdfRendered(true)}>
          <Page size="A4" wrap>
            <View style={{ position: "absolute", top: 15, right: 15 }}>
              <Image
                style={styles.logo}
                src={`${process.env.PUBLIC_URL}/static/img/icons/HD-accessibility-logo-footer-2.0.png`}
              />
            </View>
            <View style={{ width: "100%", padding: '60px 50px 0 50px'}}>
              <Text style={{ fontSize: 15, ...styles.medium}}>
                {name} - Webmaster instructions (GTM)
              </Text>
              <View style={styles.block}>
                <Text style={{fontSize: 12, ...styles.medium}}>Objective</Text>
                <Text style={{marginTop: 10, ...styles.paragraph}}>
                  The objective of the following document is to provide the necessary information and documentation on how to integrate the Handiscover Accessibility Widget into Google Tag Manager.
                </Text>
              </View>
              <View style={styles.block}>
                <Text style={{fontSize: 12, ...styles.medium}}>Script for Google Tag manager (GTM):</Text>
                <View style={{flexDirection: "column", marginTop: 10}}>
                  <View style={{ marginBottom: 4}}>
                    <CodeBlockGTM propertyId={property.id} companyAPIKey={companyAPIKey && companyAPIKey.key ? companyAPIKey.key : ''}/>
                  </View>
                </View>
              </View>
            </View>
          </Page>
          <Page size="A4" wrap>
            <View style={styles.secondPageWrapper}>
              <View style={styles.block}>
                <Text style={{fontSize: 12, ...styles.medium}}>Adding a widget using Google Tag Manager (step by step)</Text>
                <Text style={{marginTop: 10, marginBottom: 5, ...styles.paragraph}}>In Google Tag Manager:</Text>
                <View style={{flexDirection: "column"}}>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>1.</Text>
                    <Text style={styles.paragraph}>Go to your <Text style={styles.medium}>Workspace</Text></Text>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>2.</Text>
                    <Text style={styles.paragraph}>Click the <Text style={styles.medium}>Overview</Text> tab</Text>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>3.</Text>
                    <Text style={styles.paragraph}>Click <Text style={styles.medium}>Add a new tag</Text></Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-1.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>4.</Text>
                    <Text style={styles.paragraph}>Click <Text style={styles.medium}>Tag Configuration</Text></Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-2.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                </View>
              </View>
            </View>
          </Page>
          <Page size="A4" wrap>
            <View style={styles.secondPageWrapper}>
              <View style={styles.block}>
                <View style={{flexDirection: "column"}}>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>5.</Text>
                    <Text style={styles.paragraph}>Choose <Text style={styles.medium}>Custom HTML</Text></Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-3.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>6.</Text>
                    <Text style={styles.paragraph}>Now you need to <Text style={styles.medium}>copy the widget script </Text>provided by Handiscover and <Text style={styles.medium}>paste it into the box</Text></Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-4.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                </View>
              </View>
            </View>
          </Page>
          <Page size="A4" wrap>
            <View style={styles.secondPageWrapper}>
              <View style={styles.block}>
                <View style={{flexDirection: "column"}}>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>7.</Text>
                    <Text style={styles.paragraph}>Once we've inserted the script into the tag, click <Text style={styles.medium}>Triggering</Text></Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-5.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>8.</Text>
                    <Text style={styles.paragraph}>Select a trigger (<Text style={styles.medium}>All Pages</Text> is a good start)</Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-6.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                </View>
              </View>
            </View>
          </Page>
          <Page size="A4" wrap>
            <View style={styles.secondPageWrapper}>
              <View style={styles.block}>
                <View style={{flexDirection: "column"}}>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>9.</Text>
                    <Text style={styles.paragraph}>Now we can save our tag. Click the <Text style={styles.medium}>Save</Text> button</Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-7.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>10.</Text>
                    <Text style={styles.paragraph}>After clicking on the <Text style={styles.medium}>Save</Text> button, we will get a modal window. <Text style={styles.medium}>Name your future tag</Text> as you wish and click the Save button</Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-8.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                </View>
              </View>
            </View>
          </Page>
          <Page size="A4" wrap>
            <View style={styles.secondPageWrapper}>
              <View style={styles.block}>
                <View style={{flexDirection: "column"}}>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>11.</Text>
                    <Text style={styles.paragraph}>Click on the <Text style={styles.medium}>Submit</Text> button</Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-9.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                  <View style={styles.li}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>12.</Text>
                    <Text style={styles.paragraph}>And the final step is to publish our changes. <Text style={styles.medium}>Name your new version</Text> and click <Text style={styles.medium}>Save</Text></Text>
                  </View>
                  <View style={styles.imageWrapper}>
                    <Image src={'/static/img/other/guide-gtm-10.jpg'} style={{ objectFit: 'contain'}}/>
                  </View>
                </View>
              </View>
            </View>
          </Page>
        </Document>
      </PDFViewer>
      <AbsoluteLoading visible={loading}/>
    </Box>
  )
}

export default GTMInstruction